import React, { useState, useEffect, useRef, useCallback } from 'react';
import classNames from 'classnames';

import InputWrapper from 'components/inputWrapper';

import useKeyDown from 'hooks/useKeyDown'

import passwordValidator from './validator';
import TextField from '../../stateless/textField';

import './index.scss';

const PasswordField = ({
  value,
  noValidate,
  errorKey = 'password',
  setErrors,
  name,
  showTooltip,
  errorMessage,
  labelNote,
  required,
  ...rest
}) => {

  const trimmedValue = value.trim();

  const ref = useRef(null);

  const [showPassword, setShowPassword] = useState(false);
  const [hasErrors, setHasErrors] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const changePasswordVisibility = useCallback(() => setShowPassword(prev => !prev), []);

  const validate = useCallback(() => {
    const doesHaveErrors = noValidate ? false : !passwordValidator(trimmedValue);

    setHasErrors(doesHaveErrors);
    setErrors(doesHaveErrors, errorKey);
  }, [setErrors, noValidate, errorKey, trimmedValue]);

  const handleClickOutside = useCallback(callback => e => {
    if (ref.current && !ref.current.contains(e.target)) {
      callback && callback(e);
    }
  }, [ref.current]);

  useKeyDown(() => setIsFocused(false), [], { current: window }, ['Tab']);

  useEffect(() => {
    !!trimmedValue && validate();
  }, [validate, trimmedValue]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside(() => setIsFocused(false)));

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [handleClickOutside]);

  return (
    <InputWrapper
      label={name}
      showError={!!(hasErrors || errorMessage)}
      errorMessage={(hasErrors && 'Password is not valid') || (errorMessage && errorMessage)}
      labelNote={labelNote}
      required={required}
    >
      <div ref={e => ref.current = e}>
        <div
          onClick={changePasswordVisibility}
          className={classNames('icon', { 'icon-eye': !showPassword, 'icon-eye-off': showPassword })}
        />
        <TextField
          onFocus={() => setIsFocused(true)}
          type={showPassword ? 'text' : 'password'}
          placeholder={name}
          value={trimmedValue}
          {...rest}
        />
        {showTooltip && isFocused && (
          <div className="val-tooltip">
            <h5>Password must contain:</h5>
            <ul>
              <li>One lowercase character</li>
              <li>One uppercase character</li>
              <li>One number</li>
            </ul>
            <ul>
              <li>One special character</li>
              <li>8 characters minimum</li>
            </ul>
          </div>
        )}
      </div>
    </InputWrapper>
  );
};

export default PasswordField;
